import { ColDef } from 'ag-grid-community';
import { AgGridCollapsibleComponent } from '../../../../ag-grid-custom-cell/collapsible/ag-grid-collapsible.component';
import { AgGridBadgeComponent } from '../../../../ag-grid-custom-cell/badge/ag-grid-badge.component';

const guidWidth = 400;

export enum StatusOptions {
  active = 'active',
  override = 'override',
  configurationOverride = 'configuration override',
  conflict = 'conflict',
  excluded = 'excluded'
}

const BG_BADGE_NEUTRAL = 'bg-neutral-soft';
const BG_DEFAULT = 'var(--df-color-neutral-alt-default-background, #FFFFFF)';
const BG_HIGHLIGHT = 'var(--df-color-warning-subtle-background, #FFF0E6)';
const BG_DANGER = 'var(--df-color-danger-subtle-background, #FFEEED)';

export const badgeMap = {
  [StatusOptions.active]: BG_BADGE_NEUTRAL,
  [StatusOptions.override]: BG_BADGE_NEUTRAL,
  [StatusOptions.configurationOverride]: BG_BADGE_NEUTRAL,
  [StatusOptions.conflict]: 'bg-danger',
  [StatusOptions.excluded]: 'bg-warning'
};

export const backgroundMap = {
  [StatusOptions.excluded]: BG_HIGHLIGHT,
  [StatusOptions.conflict]: BG_DANGER,
  [StatusOptions.active]: BG_DEFAULT,
  [StatusOptions.override]: BG_HIGHLIGHT,
  [StatusOptions.configurationOverride]: BG_HIGHLIGHT
};

export const commentsMap: Record<StatusOptions, (actorName: string) => string> = {
  [StatusOptions.excluded]: (actorName) => `${actorName} is excluded`,
  [StatusOptions.active]: () => '-',
  [StatusOptions.override]: () => '-',
  [StatusOptions.configurationOverride]: (actorName) => `Configuration override at ${actorName}`,
  [StatusOptions.conflict]: () => '-',
};

export const BAD_FORMAT = 'Bad format';

export const columnDefs: ColDef[] = [
  {
    field: 'parameterName', headerName: 'Parameter Name', sortable: true, filter: true,
    valueGetter: (row) => row.data.parameterName,
  }, {
    field: 'parameterValue', headerName: 'Parameter Value', sortable: true, filter: true,
    valueGetter: (row) => row.data.parameterValue,
  },
  {
    field: 'url', headerName: 'URL', sortable: true, filter: true,
    valueGetter: (row) => row.data.url,
  },
  {
    field: 'launchType', headerName: 'Launch Type', sortable: true, filter: true,
    valueGetter: (row) => row.data.launchType
  },
  {
    field: 'modality', headerName: 'Modality', sortable: true, filter: true,
    valueGetter: (row) => row.data.modality
  },
  {
    field: 'httpsMethod', headerName: 'HTTPS Method', sortable: true, filter: true,
    valueGetter: (row) => row.data.httpsMethod
  },
  {
    field: 'status', headerName: 'Status', sortable: true, filter: true,
    valueGetter: (row) => row.data.status,
    cellRenderer: AgGridBadgeComponent,
    cellRendererParams: { badgeMap }
  },
  {
    field: 'comment', headerName: 'Comment', sortable: true, filter: true,
    valueGetter: (row) => row.data.comment
  },
  {
    field: 'componentContent', headerName: 'Component Content', sortable: true, filter: true,
    autoHeight: true, wrapText: true,
    valueGetter: (row) => row.data.componentContent,
    cellRenderer: AgGridCollapsibleComponent,
    cellRendererParams: {isCollapsed: true,},
    suppressKeyboardEvent: (event) => {
      if (event.event.key === 'Enter' || event.event.key === 'Space') {
        event.node.setExpanded(!event.node.expanded);
      }
      return false;
    },
    onCellDoubleClicked: (event) => event.node.setExpanded(!event.node.expanded)
  },
  {
    field: 'appType', headerName: 'App Type', sortable: true, filter: true,
    valueGetter: (row) => row.data.appType,
  },
  {
    field: 'appId', headerName: 'App Id', sortable: true, filter: true,
    width: guidWidth,
    valueGetter: (row) => row.data.appId
  },
  {
    field: 'appName', headerName: 'App Name', sortable: true, filter: true,
    valueGetter: (row) => row.data.appName
  },
  {
    field: 'componentType', headerName: 'Component Type', sortable: true, filter: true,
    valueGetter: (row) => row.data.componentType
  },
  {
    field: 'componentId', headerName: 'Component Id', sortable: true, filter: true,
    width: guidWidth,
    valueGetter: (row) => row.data.componentId
  },
  {
    field: 'componentName', headerName: 'Component Name', sortable: true, filter: true,
    valueGetter: (row) => row.data.componentName
  },
  {
    field: 'actorType', headerName: 'Actor Type', sortable: true, filter: true,
    valueGetter: (row) => row.data.actorType
  },
  {
    field: 'actorName', headerName: 'Actor Name', sortable: true, filter: true,
    valueGetter: (row) => row.data.actorName
  },
  {
    field: 'deploymentTimestamp', headerName: 'Last Update', sortable: true, filter: 'agDateColumnFilter',
    filterParams: {
      comparator: (filterLocaleDateAtMidnight: Date, cellValue: string): number => {
        const cellDate = new Date(cellValue);
        if (cellDate < filterLocaleDateAtMidnight) {
          return -1;
        } else if (cellDate > filterLocaleDateAtMidnight) {
          return 1;
        }
        return 0;
      }
    },
    valueGetter: (row) => row.data.deploymentTimestamp
  },
  {
    field: 'configurationId', headerName: 'Configuration Id', sortable: true, filter: true,
    width: guidWidth,
    valueGetter: (row) => row.data.configurationId
  },
  {
    field: 'configurationName', headerName: 'Configuration Name', sortable: true, filter: true,
    valueGetter: (row) => row.data.configurationName
  },
  {
    field: 'configurationContent', headerName: 'Configuration Content', sortable: true, filter: true,
    autoHeight: true, wrapText: true,
    valueGetter: (row) => row.data.configurationContent,
    cellRenderer: AgGridCollapsibleComponent,
    cellRendererParams: {isCollapsed: true,},
    suppressKeyboardEvent: (event) => {
      if (event.event.key === 'Enter' || event.event.key === 'Space') {
        event.node.setExpanded(!event.node.expanded);
      }
      return false;
    },
    onCellDoubleClicked: (event) => event.node.setExpanded(!event.node.expanded)
  }
];
